<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">{{ $t(`chapter9.title`) }}</p>

    <!-- Incl. UMF -->
    <fieldset class="box">
      <legend>{{ $t('chapter9.q0.title') }}</legend>
      <p class="multiline">
        {{ $t('chapter9.q0.text1') }}
      </p>
      <b-field :label="$t('chapter9.q0.label')">
        <b-input expanded v-model="study.verantwortlicheUndDsb.abweichungen" @blur="save" />
      </b-field>
      <p class="multiline">
        {{ $t('chapter9.q0.text2') }}
        <br />
        <i18n-t keypath="chapter9.q0.text2GU" tag="span" scope="global">
          <template #link>
            <MailtoLink user="dsb" domain="uni-frankfurt.de" />
          </template>
        </i18n-t>
        <br />
        <i18n-t keypath="chapter9.q0.text2UMF" tag="span" scope="global">
          <template #link>
            <MailtoLink user="datenschutz" domain="unimedizin-ffm.de" />
          </template>
        </i18n-t>
        <br />
        {{ $t('chapter9.q0.text2HBDI') }}
      </p>
    </fieldset>

    <!-- Beteiligte Institute -->
    <fieldset class="box">
      <legend>
        {{ $t('chapter9.q1.title') }}
        <b-tooltip :label="$t(`general.showRechtstext`)" position="is-right" type="is-warning">
          <b-icon
            type="is-warning"
            icon="format-section"
            class="legal-toggle"
            :class="{ 'is-active': showLegal_c9q1 }"
            @click.native="showLegal_c9q1 = !showLegal_c9q1"
          />
        </b-tooltip>
      </legend>
      <p>{{ $t('chapter9.q1.text') }}</p>

      <div v-for="option in beteiligteInstituteOptions" :key="option">
        <b-tooltip
          :active="isAutoSelected(option)"
          :label="autoSelectedTooltip(option)"
          position="is-right"
          type="is-info"
          multilined
        >
          <b-checkbox
            :native-value="option"
            v-model="study.verantwortlicheUndDsb.beteiligteInstitute"
            :disabled="isAutoSelected(option)"
            @change="save"
          >
            {{ $t(`chapter9.q1.${option}`) }}
          </b-checkbox>
        </b-tooltip>
      </div>

      <RechtsText
        v-if="
          study.verantwortlicheUndDsb.beteiligteInstitute !== undefined &&
          study.verantwortlicheUndDsb.beteiligteInstitute.includes('diz') &&
          showLegal_c9q1
        "
      >
        {{ $t('rechtstexte.c9q1a') }}
      </RechtsText>
      <RechtsText
        v-if="
          study.verantwortlicheUndDsb.beteiligteInstitute !== undefined &&
          study.verantwortlicheUndDsb.beteiligteInstitute.includes('ibdf') &&
          showLegal_c9q1
        "
      >
        {{ $t('rechtstexte.c9q1b') }}
      </RechtsText>
    </fieldset>

    <!-- Externe Datengeber -->
    <fieldset class="box" v-if="showExterneDatengeber">
      <legend>
        {{ $t('chapter9.q3.title') }}
      </legend>
      <p>
        <i18n-t keypath="chapter9.q3.text" tag="span" scope="global">
          <template #link>
            <a href="#chapter7" class="chapter-link" @click.prevent="scrollToChapter('chapter7')">
              {{ $t('chapter9.q3.textLink') }}
              <b-icon icon="link-variant" size="is-small" />
            </a>
          </template>
        </i18n-t>
      </p>
      <b-field>
        <b-input v-model="study.verantwortlicheUndDsb.externeDatengeber" @blur="save" />
      </b-field>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStudyStore } from '@/stores/studyStore'
import { useRechtstextToggle } from '@/composables/useVisibilityToggles'
import RechtsText from '@/components/RechtsText.vue'
import MailtoLink from '@/components/MailtoLink.vue'
import { BCheckbox, BField, BIcon, BInput, BTooltip } from 'buefy'

defineProps<{ id: string; modelValue?: object }>()

const scrollToChapter = inject<(id: string) => void>('scrollToChapter')!

const store = useStudyStore()
const study = store.study
const title = 'Verantwortliche und DSB'
const { t } = useI18n()

const beteiligteInstituteOptions = ['diz', 'ibdf', 'cobic']

const showExterneDatengeber = computed(() =>
  (study.datenherkunft.datenQuelleExtern || []).includes('sonstige'),
)

// "diz" and "ibdf" are auto-selected (and locked) when other chapters imply the institute's
// involvement; each reason is surfaced in the checkbox tooltip.
const dizAutoSelectReasons = computed<string[]>(() => {
  const reasons: string[] = []
  if (study.datenherkunft.datenQuellen.includes('diz')) {
    reasons.push(t('chapter9.q1.autoReasons.datenherkunftDiz'))
  }
  if (study.datenspeicherung.pseudonymisierung.includes('diz')) {
    reasons.push(t('chapter9.q1.autoReasons.pseudonymisierungDiz'))
  }
  if (study.datenspeicherung.speicherungWaehrendErhebung.includes('klinet')) {
    reasons.push(t('chapter9.q1.autoReasons.speicherungKlinet'))
  }
  if (study.archivierungLoeschungAnonymisierung.aalPersonenbezogeneDaten === 'diz') {
    reasons.push(t('chapter9.q1.autoReasons.archivierungDiz'))
  }
  if (study.TOMs.benoetigteBerechtigungen.includes('klifo')) {
    reasons.push(t('chapter9.q1.autoReasons.berechtigungenKlifo'))
  }
  return reasons
})

const ibdfAutoSelectReasons = computed<string[]>(() => {
  const reasons: string[] = []
  if (study.datenbeschreibung.bioProbenNachErfuellung.includes('anIbdfUebergeben')) {
    reasons.push(t('chapter9.q1.autoReasons.bioProbenIbdf'))
  }
  if (study.datenherkunft.datenQuellen.includes('ibdf')) {
    reasons.push(t('chapter9.q1.autoReasons.datenherkunftIbdf'))
  }
  if (study.datenherkunft.quellSysteme.includes('biobankCentraxx')) {
    reasons.push(t('chapter9.q1.autoReasons.quellSystemeBiobankCentraxx'))
  }
  if (study.datenspeicherung.pseudonymisierung.includes('ibdf')) {
    reasons.push(t('chapter9.q1.autoReasons.pseudonymisierungIbdf'))
  }
  return reasons
})

function autoSelectReasonsFor(option: string): string[] {
  if (option === 'diz') return dizAutoSelectReasons.value
  if (option === 'ibdf') return ibdfAutoSelectReasons.value
  return []
}

function isAutoSelected(option: string): boolean {
  return autoSelectReasonsFor(option).length > 0
}

function autoSelectedTooltip(option: string): string {
  return t('chapter9.q1.autoSelectedTooltip', {
    reasons: autoSelectReasonsFor(option).join('; '),
  })
}

const save = () => {
  store.updateChapter('verantwortlicheUndDsb', study.verantwortlicheUndDsb)
}

watch(
  [dizAutoSelectReasons, ibdfAutoSelectReasons],
  ([dizReasons, ibdfReasons]) => {
    const current = study.verantwortlicheUndDsb.beteiligteInstitute
    let changed = false
    if (dizReasons.length > 0 && !current.includes('diz')) {
      current.push('diz')
      changed = true
    }
    if (ibdfReasons.length > 0 && !current.includes('ibdf')) {
      current.push('ibdf')
      changed = true
    }
    if (changed) save()
  },
  { immediate: true },
)

const showLegal_c9q1 = useRechtstextToggle()
</script>

<style scoped>
.multiline {
  white-space: pre-line;
}

.chapter-link {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
}
</style>
